/**
 * Honorarkräfte (freie Mitarbeit) – eigenständiges Modul, getrennt von den
 * Angestellten. Honorarkräfte haben keinen Urlaubsanspruch; Vergütung läuft
 * über Honorarverträge (Stundensatz und/oder Pauschale). Sie können als
 * Einzelperson oder über eine Firma kommen und an mehreren Standorten arbeiten.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Briefcase, Plus, Pencil, Trash2, FileSignature, Loader2, User, Building2, Search, FileDown } from 'lucide-react';
import { honorarkraefteAPI, honorarfirmenAPI, locationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { HonorarkraftDialog } from './honorarkraefte/HonorarkraftDialog';
import { ContractsDialog } from './honorarkraefte/ContractsDialog';
import { FirmenTab } from './honorarkraefte/FirmenTab';
import { AuswertungTab } from './honorarkraefte/AuswertungTab';
import { HonorarTemplatesTab } from './honorarkraefte/HonorarTemplatesTab';
import { HonorarDocGenerateDialog } from './honorarkraefte/HonorarDocGenerateDialog';
import { XlsxImportDialog } from '../components/XlsxImportDialog';
import { Upload } from 'lucide-react';

const fmtEUR = (v) => (v == null ? '' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v));

const contractLabel = (c) => {
  if (!c) return null;
  const parts = [];
  if (c.stundensatz != null) parts.push(`${fmtEUR(c.stundensatz)}/${c.abrechnungsart === 'UE' ? 'UE' : 'Std.'}`);
  if (c.pauschale != null) parts.push(`Pauschale ${fmtEUR(c.pauschale)}`);
  return parts.join(' · ') || 'Vertrag';
};

export default function Honorarkraefte() {
  const [rows, setRows] = useState([]);
  const [firmen, setFirmen] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [contractsFor, setContractsFor] = useState(null);
  const [docGenFor, setDocGenFor] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [hRes, fRes, lRes] = await Promise.all([
        honorarkraefteAPI.getAll(locFilter && locFilter !== 'all' ? { location_id: locFilter } : {}),
        honorarfirmenAPI.getAll(),
        locationsAPI.getAll({ is_active: true }),
      ]);
      setRows(hRes.data || []);
      setFirmen(fRes.data || []);
      setLocations(lRes.data || []);
    } catch {
      toast.error('Fehler beim Laden der Honorarkräfte');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [locFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(q)
      || (r.firma_name || '').toLowerCase().includes(q)
      || (r.positions || []).some((p) => p.toLowerCase().includes(q))
    ));
  }, [rows, search]);

  const openCreate = () => { setEditData(null); setDialogOpen(true); };
  const openEdit = (r) => { setEditData(r); setDialogOpen(true); };

  const confirmDelete = async () => {
    try {
      await honorarkraefteAPI.delete(delRow.id);
      toast.success('Honorarkraft entfernt');
      setDelRow(null);
      load();
    } catch { toast.error('Fehler beim Entfernen'); }
  };

  return (
    <MaybeLayout>
      <div className="space-y-6" data-testid="honorarkraefte-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-primary" />
              Honorarkräfte
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Freie Mitarbeit (selbstständig) – ohne Urlaubsanspruch, Vergütung über Honorarverträge. Einzelpersonen oder über Firmen, einsetzbar an mehreren Standorten.
            </p>
          </div>
        </div>

        <Tabs defaultValue="kraefte">
          <TabsList>
            <TabsTrigger value="kraefte" data-testid="tab-kraefte">Honorarkräfte</TabsTrigger>
            <TabsTrigger value="firmen" data-testid="tab-firmen">Firmen</TabsTrigger>
            <TabsTrigger value="auswertung" data-testid="tab-auswertung">Honorar-Auswertung</TabsTrigger>
            <TabsTrigger value="vorlagen" data-testid="tab-vorlagen">Vorlagen</TabsTrigger>
          </TabsList>

          <TabsContent value="kraefte" className="mt-4">
            <Card className="border-border/60">
              <CardHeader className="border-b border-border/40 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="font-heading text-lg">Übersicht</CardTitle>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 min-w-0 sm:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen…" className="pl-9 w-full" data-testid="honorarkraft-search" />
                    </div>
                    <Button variant="outline" className="rounded-full w-full sm:w-auto whitespace-nowrap" onClick={() => setImportOpen(true)} data-testid="import-honorarkraefte-btn"><Upload className="h-4 w-4 mr-2" /> Import (XLSX)</Button>
                    <Select value={locFilter} onValueChange={setLocFilter}>
                      <SelectTrigger className="w-full sm:w-[180px] rounded-full" data-testid="honorarkraft-loc-filter">
                        <SelectValue placeholder="Alle Standorte" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Standorte</SelectItem>
                        {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button className="rounded-full w-full sm:w-auto whitespace-nowrap" onClick={openCreate} data-testid="add-honorarkraft-btn"><Plus className="h-4 w-4 mr-2" /> Honorarkraft</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                ) : filtered.length === 0 ? (
                  <div className="p-10 text-center text-muted-foreground" data-testid="honorarkraefte-empty">
                    <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p>Noch keine Honorarkräfte erfasst.</p>
                    <Button variant="link" onClick={openCreate}>Erste Honorarkraft anlegen</Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead className="hidden md:table-cell">Art</TableHead>
                          <TableHead className="hidden lg:table-cell">Funktionen</TableHead>
                          <TableHead className="hidden lg:table-cell">Standorte</TableHead>
                          <TableHead>Aktueller Vertrag</TableHead>
                          <TableHead className="text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((r) => (
                          <TableRow key={r.id} data-testid={`honorarkraft-row-${r.id}`}>
                            <TableCell className="font-medium">
                              {r.last_name}, {r.first_name}
                              {!r.is_active && <Badge variant="outline" className="ml-2 text-xs">inaktiv</Badge>}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {r.type === 'firma' ? (
                                <span className="inline-flex items-center gap-1 text-sm"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {r.firma_name || 'Firma'}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-sm"><User className="h-3.5 w-3.5 text-muted-foreground" /> Einzelperson</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="flex flex-wrap gap-1">
                                {(r.positions || []).map((p) => (<Badge key={p} variant="secondary" className="text-xs">{p}</Badge>))}
                                {(r.positions || []).length === 0 && <span className="text-xs text-muted-foreground">–</span>}
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{(r.location_names || []).join(', ') || '–'}</TableCell>
                            <TableCell>
                              {r.active_contract ? (
                                <Badge variant="secondary" className="text-xs">{contractLabel(r.active_contract)}</Badge>
                              ) : <span className="text-xs text-amber-600">kein Vertrag</span>}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button size="icon" variant="ghost" title="Verträge" onClick={() => setContractsFor(r)} data-testid={`contracts-honorarkraft-${r.id}`}><FileSignature className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" title="Dokument erzeugen" onClick={() => setDocGenFor(r)} data-testid={`generate-doc-honorarkraft-${r.id}`}><FileDown className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-honorarkraft-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDelRow(r)} data-testid={`delete-honorarkraft-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="firmen" className="mt-4">
            <Card className="border-border/60">
              <CardContent className="p-4">
                <FirmenTab firmen={firmen} reload={load} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="auswertung" className="mt-4">
            <Card className="border-border/60">
              <CardContent className="p-4">
                <AuswertungTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vorlagen" className="mt-4">
            <Card className="border-border/60">
              <CardContent className="p-4">
                <HonorarTemplatesTab />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <HonorarDocGenerateDialog
        open={!!docGenFor}
        onOpenChange={(o) => { if (!o) setDocGenFor(null); }}
        honorarkraft={docGenFor}
      />

      <HonorarkraftDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        editData={editData}
        firmen={firmen}
        locations={locations}
      />

      <ContractsDialog
        open={!!contractsFor}
        onClose={() => setContractsFor(null)}
        honorarkraft={contractsFor}
        onChanged={load}
      />

      <XlsxImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Honorarkräfte importieren"
        description="Excel hochladen → Vorschau prüfen → bestätigen. Abgleich über Vorname + Nachname."
        infoLines={[
          'Pflichtfelder: Vorname, Nachname.',
          'Bestehende Honorarkräfte werden über den Namen erkannt und aktualisiert (nur ausgefüllte Zellen).',
          'Art: Einzelperson oder Firma (Firma per exaktem Namen). Funktionen/Standorte mit Komma trennen.',
        ]}
        templateUrl="/honorarkraefte/import/template"
        previewUrl="/honorarkraefte/import/preview"
        commitUrl="/honorarkraefte/import"
        templateFilename="Vorlage_Honorarkraefte.xlsx"
        onImported={load}
      />

      <AlertDialog open={!!delRow} onOpenChange={(o) => { if (!o) setDelRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Honorarkraft entfernen?</AlertDialogTitle>
            <AlertDialogDescription>{delRow?.first_name} {delRow?.last_name} wird inkl. aller hinterlegten Verträge entfernt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-honorarkraft">Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MaybeLayout>
  );
}
